import { useMemo } from "react";
import { Button, Popover, Tag, Tooltip, Typography } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

import { formatTrackerCurrency, getRowPopoverKey } from "../tracker.utils";


const { Text } = Typography;



/** Builds the Milestones table column definitions (memoized on the callbacks/open popover key). */
export function useMilestoneColumns({
  popoverOpenKey,
  onPopoverOpenChange,
  onViewDetails,
  onUpdateStatusClick,
}) {

  const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const raw = String(dateStr).trim();
  if (!raw || raw === "N/A") return "-";

  const ymd = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (ymd) {
    const [, year, month, day] = ymd;
    return `${month}-${day}-${year}`;
  }

  const dashDate = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (dashDate) {
    const [, first, second, year] = dashDate;
    const firstNum = Number(first);
    const secondNum = Number(second);
    if (firstNum > 12 && secondNum <= 12) {
      return `${second}-${first}-${year}`;
    }
    return `${first}-${second}-${year}`;
  }

  const mdy = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (mdy) {
    const [, month, day, year] = mdy;
    return `${month}-${day}-${year}`;
  }

  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    const day = String(parsed.getDate()).padStart(2, "0");
    const year = parsed.getFullYear();
    return `${month}-${day}-${year}`;
  }

  return raw;
};
  return useMemo(
    () => [
      {
        title: "Project ID - SOW ID",
        key: "projectSow",
        width: "13%",
        align: "left",
        render: (_, record) => (
          <div>
            <div style={{ fontSize: 12, fontWeight: 600 }}>
              {record.projectId || "-"}
            </div>
            <div style={{ fontSize: 11, color: "#8c8c8c" }}>
              {record.sowId || "-"}
            </div>
          </div>
        ),
      },
      {
        title: "ID",
        dataIndex: "milestoneId",
        key: "milestoneId",
        align: "left",
        width: "6%",
        render: (v) => (
          <Tooltip title={v}>
            <Text style={{ fontSize: 13 ,fontWeight:"normal"}} ellipsis>
              {v || "-"}
            </Text>
          </Tooltip>
        ),
      },
      {
        title: "Milestone Name",
        dataIndex: "milestoneName",
        key: "milestoneName",
        align: "left",
        width: "25%",
        render: (v) => (
          <Tooltip title={v}>
            <Typography.Paragraph
              style={{
                marginBottom: 0,
                fontSize: 13,
                fontWeight:"normal",
                lineHeight: "18px",
              }}
              ellipsis={{
                rows: 2,
                tooltip: v,
              }}
            >
              {v || "-"}
            </Typography.Paragraph>
          </Tooltip>
        ),
      },
      {
        title: "Account Name",
        dataIndex: "accountName",
        key: "accountName",
        align: "left",
        width: "18%",
        render: (v) => (
          <Tooltip title={v}>
            <Typography.Paragraph
              style={{
                marginBottom: 0,
                fontSize: 13,
                fontWeight:"normal",
                lineHeight: "18px",
              }}
              ellipsis={{
                rows: 2,
                tooltip: v,
              }}
            >
              {v || "-"}
            </Typography.Paragraph>
          </Tooltip>
        ),
      },
      {
        title: "Start Date",
        dataIndex: "startDate",
        key: "startDate",
        align: "left",
        width: "10%",
        render: (v) => <Text style={{ fontSize: 12,fontWeight:"normal", }}>{formatDate(v)}</Text>,
      },
      {
        title: "End Date",
        dataIndex: "endDate",
        key: "endDate",
        align: "left",
        width: "10%",
        render: (v) => <Text style={{ fontSize: 12,fontWeight:"normal", }}>{formatDate(v)}</Text>,
      },
      {
        title: "Amount",
        dataIndex: "amount",
        key: "amount",
        width: "10%",
        align: "left",
        render: (v) => formatTrackerCurrency(v ?? 0),
      },
      {
        title: "Status",
        key: "milestoneStatus",
        align: "left",
        width: "10%",
        render: (_, r) => <Text style={{ fontSize: 12,fontWeight:"normal", }}>{r.status || "-"}</Text>,
      },
      {
        title: "",
        key: "actions",
        width: "3%",
        align: "left",
        render: (_, r, index) => {
          const popoverKey = getRowPopoverKey(r, index);
          return (
            <Popover
              content={
                <div style={{ width: 380 }}>
                  <div style={{ marginBottom: 12 }}>
                    <Text strong style={{ fontSize: 13, display: "block", marginBottom: 8 }}>
                      Milestone Details
                    </Text>
                    <Text style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
                      <span style={{ color: "#8c8c8c" }}>Milestone:</span>{" "}
                      <span style={{ fontWeight: 600 }}>{r.milestoneName || "-"}</span>
                    </Text>
                    <Text style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
                      <span style={{ color: "#8c8c8c" }}>Project ID:</span>{" "}
                      <Tag color="blue">{r.projectId || "-"}</Tag>
                    </Text>
                    <Text style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
                      <span style={{ color: "#8c8c8c", fontWeight: 600 }}>Amount:</span>{" "}
                      <span style={{ color: "#1677ff", fontWeight: 600 }}>
                        {formatTrackerCurrency(r.amount ?? 0)}
                      </span>
                    </Text>
                  </div>

                  <div style={{ marginBottom: 12 }}>
                    <Text strong style={{ fontSize: 13, display: "block", marginBottom: 8 }}>
                      Current Status
                    </Text>
                    <Tag style={{ fontSize: 12 }}>{r.status || "-"}</Tag>
                  </div>

                  <Button
                    type="primary"
                    block
                    onClick={() => {
                      onPopoverOpenChange(null);
                      onUpdateStatusClick(r);
                    }}
                  >
                    Update Milestone Status
                  </Button>
                </div>
              }
              title="Milestone Information"
              trigger="click"
              open={popoverOpenKey === popoverKey}
              onOpenChange={(open) => onPopoverOpenChange(open ? popoverKey : null)}
              placement="topLeft"
              overlayClassName="trackon-root"
              overlayStyle={{ maxWidth: 420 }}
            >
              <Button
                icon={<InfoCircleOutlined />}
                size="small"
                type="text"
                onClick={() => onViewDetails(r)}
              />
            </Popover>
          );
        },
      },
    ],
    [popoverOpenKey, onPopoverOpenChange, onViewDetails, onUpdateStatusClick],
  );
}
